import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { CreateEmailTemplateRPC } from "@pulse/pulse-rpcs/lib/email-cycle/createEmailTemplateRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useCreateEmailTemplateRPCClientImpl } from "../rpcs/RPC";
import { TemplateContent } from "@pulse/pulse-rpcs/lib/email-cycle/templateContent";
import {
  BoundedNameModel,
  createBoundedNameModel,
} from "../../models/BoundedNameModel";
import {
  createEmailTemplateContentModel,
  EmailTemplateContentModel,
} from "../models/EmailTemplateContentModel";
import { BoundedName } from "@pulse/pulse-rpcs/lib/types/boundedName";
import { BoundedNameValidationError } from "../../enums/BoundedNameValidationError";

type CreateEmailTemplateRPCError = Exclude<
  CreateEmailTemplateRPC.RPCError,
  CreateEmailTemplateRPC.RPCError.DuplicateTemplateName
>;

const getInitialState = (): SnapshotIn<typeof CreateEmailTemplateStore> => ({
  emailTemplateName: createBoundedNameModel(),
  emailTemplateContent: createEmailTemplateContentModel(),
  projectId: null,
  loadingCreateEmailTemplate: false,
  createEmailTemplateRPCError: null,
});

export const CreateEmailTemplateStore = types
  .model("CreateEmailTemplateStore", {
    emailTemplateName: BoundedNameModel,
    emailTemplateContent: EmailTemplateContentModel,
    projectId: types.maybeNull(types.string),
    loadingCreateEmailTemplate: types.boolean,
    createEmailTemplateRPCError: types.maybeNull(
      types.enumeration(
        "CreateEmailTemplateRPCError",
        Object.values(CreateEmailTemplateRPC.RPCError).filter(
          (error): error is CreateEmailTemplateRPCError =>
            CreateEmailTemplateRPC.RPCError.DuplicateTemplateName !== error,
        ),
      ),
    ),
  })
  .views((store) => ({
    get disableSubmit(): boolean {
      const isEmptyData =
        !store.emailTemplateName.name || !store.emailTemplateContent.content;
      const hasValidationError =
        store.emailTemplateName.validationError ??
        store.emailTemplateContent.validationError;
      return !!(isEmptyData || hasValidationError);
    },
  }))
  .actions((store) => ({
    setProjectId: (projectId: string | null): void => {
      store.projectId = projectId;
    },
    createEmailTemplate: flow(function* () {
      try {
        store.loadingCreateEmailTemplate = true;
        store.createEmailTemplateRPCError = null;

        const apiClient = getAPIClient(store);
        const request = new CreateEmailTemplateRPC.Request(
          new BoundedName(store.emailTemplateName.name),
          new TemplateContent(store.emailTemplateContent.content),
          store.projectId ? new LeoUUID(store.projectId) : null,
        );

        const result: LeoRPCResult<
          CreateEmailTemplateRPC.Response,
          CreateEmailTemplateRPC.Errors.Errors
        > =
          yield useCreateEmailTemplateRPCClientImpl(apiClient).execute(request);
        if ("response" in result) {
          // Nothing to handle on successful response
        } else {
          switch (result.error.code) {
            case CreateEmailTemplateRPC.RPCError.DuplicateTemplateName:
              store.emailTemplateName.validationError =
                BoundedNameValidationError.DuplicateName;
              break;
            case CreateEmailTemplateRPC.RPCError.InvalidProjectId:
            case CreateEmailTemplateRPC.RPCError.ProjectAlreadyArchived:
              store.createEmailTemplateRPCError = result.error.code;
              break;
            default:
              result.error.code satisfies never;
          }
        }
      } finally {
        store.loadingCreateEmailTemplate = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createCreateEmailTemplateStore = (): Instance<
  typeof CreateEmailTemplateStore
> => CreateEmailTemplateStore.create(getInitialState());
